import { BANKS, calculateOfferedRate, canTakeLoan, getLoyaltyLevel, getAvailablePlacements, calculateCreditScore, CREDIT_SCORES, SCORE_REVEAL_MESSAGES, getRevenueFloor } from '../../data/bankData';
import { calculateLoanPayment } from '../../utils/format';
import { generateId } from '../helpers';
import { getFinanceSender } from '../helpers/financeSender';
import { LEGAL_FORMS } from '../../data/legalForms';
import { VEHICLES } from '../../data/vehicles';

export const createBankSlice = (set, get) => ({
  // Bank relationships
  bankRelations: {
    bnb_mariba: { loansRepaid: 0, totalBorrowed: 0, activeLoanCount: 0, firstLoanDay: null, loyaltyLevel: 0 },
    societe_generaliste: { loansRepaid: 0, totalBorrowed: 0, activeLoanCount: 0, firstLoanDay: null, loyaltyLevel: 0 },
    credit_agricool: { loansRepaid: 0, totalBorrowed: 0, activeLoanCount: 0, firstLoanDay: null, loyaltyLevel: 0 },
  },

  // Active placements
  activePlacements: [],

  // Pending loan proposals
  pendingProposals: null,

  // Credit scoring
  creditScore: null,
  scoreRevealed: false,
  scoreRevealMessage: null,
  scoreRevealDismissed: false,

  // 13/07/2026 — Arc « crédit véhicule » : dossier de financement affecté
  // en cours (posé par eventSlice quand un achat comptant dépasserait le
  // découvert autorisé). { vehicleDataId, brand, model, price, wear,
  // offeredDay, expiresDay, sourceEventId }
  pendingVehicleFinancing: null,

  // Request loan proposals from all 3 banks
  requestLoanProposals: (amount, durationMonths) => {
    const state = get();
    const totalDebt = (state.credits || []).reduce((s, c) => s + (c.remainingPrincipal || 0), 0);
    const context = {
      reputation: state.reputation || 50,
      vehicleCount: (state.vehicles || []).length,
      monthlyRevenue: state.monthlyRevenues || 0,
      // 25/06/2026 hotfix bug user — pass the monthlyHistory so banks
      // average the last 4 months' CA instead of relying solely on the
      // (potentially just-reset) current month's value.
      monthlyHistory: state.monthlyHistory || [],
      totalDebt,
      treasury: state.treasury,
      totalDays: state.totalDays || 0,
    };

    const proposals = Object.keys(BANKS).map(bankId => {
      const rel = state.bankRelations[bankId];
      const loyaltyLevel = getLoyaltyLevel(bankId, rel.loansRepaid);
      const eligibility = canTakeLoan(bankId, { ...context });

      if (!eligibility.allowed) {
        return { bankId, eligible: false, reason: eligibility.reason };
      }

      const effectiveAmount = Math.min(amount, eligibility.maxAmount);
      const creditScoreGrade = state.creditScore?.grade || null;
      let rate = calculateOfferedRate(bankId, { ...context, loyaltyLevel, loanAmount: effectiveAmount, creditScoreGrade });
      // negociation_taux unlock (Tristan Midal DAF) — -1 percentage point on
      // any new loan rate, floored at 1.5 %.
      if (typeof get().hasSupportUnlock === 'function' && get().hasSupportUnlock('negociation_taux')) {
        rate = Math.max(0.015, rate - 0.01);
      }
      // Legal-form credibility modifier (Sujet 2): SAS = -0.5%, SARL = -0.25%,
      // EI = 0, Auto = +1%. Floored at 1% to keep some friction.
      try {
        const legalForm = state.company?.legalForm;
        if (legalForm && LEGAL_FORMS && LEGAL_FORMS[legalForm]) {
          const mod = LEGAL_FORMS[legalForm].banqueRateModifier || 0;
          rate = Math.max(0.01, rate + (mod / 100));
        }
      } catch (err) { console.warn('[bank] legal-form rate modifier failed:', err); }
      const monthly = calculateLoanPayment(effectiveAmount, rate, durationMonths);
      const approvalMsg = BANKS[bankId].advisor.loanApproval[Math.floor(Math.random() * BANKS[bankId].advisor.loanApproval.length)];

      return {
        bankId,
        eligible: true,
        amount: effectiveAmount,
        rate,
        ratePercent: (rate * 100).toFixed(2),
        monthly: Math.round(monthly),
        totalCost: Math.round(monthly * durationMonths),
        totalInterest: Math.round(monthly * durationMonths - effectiveAmount),
        loyaltyLevel,
        maxAmount: eligibility.maxAmount,
        approvalMessage: approvalMsg,
        capacityDetail: {
          maxDebt: eligibility.maxDebt,
          currentDebt: context.totalDebt,
          remaining: eligibility.maxAmount,
          requestedAmount: amount,
          ratioLabel: `4× le CA mensuel`,
        },
      };
    });

    set({ pendingProposals: { amount, durationMonths, proposals } });
  },

  // Accept a bank's loan proposal
  acceptLoanProposal: (bankId) => {
    const state = get();
    if (!state.pendingProposals) return false;
    const proposal = state.pendingProposals.proposals.find(p => p.bankId === bankId);
    if (!proposal || !proposal.eligible) return false;

    const loan = {
      id: generateId(),
      bankId,
      principal: proposal.amount,
      annualRate: proposal.rate,
      durationMonths: state.pendingProposals.durationMonths,
      monthlyPayment: proposal.monthly,
      remainingMonths: state.pendingProposals.durationMonths,
      remainingPrincipal: proposal.amount,
      startDay: state.totalDays,
    };

    const rel = { ...state.bankRelations[bankId] };
    rel.totalBorrowed += proposal.amount;
    rel.activeLoanCount += 1;
    if (!rel.firstLoanDay) rel.firstLoanDay = state.totalDays;

    set({
      credits: [...state.credits, loan],
      treasury: state.treasury + proposal.amount,
      bankRelations: { ...state.bankRelations, [bankId]: rel },
      pendingProposals: null,
    });
    return true;
  },

  dismissProposals: () => {
    set({ pendingProposals: null });
  },

  /* ── 13/07/2026 — Arc « crédit véhicule » (confrère retraité) ─────────
   * Crédit AFFECTÉ gagé sur le véhicule d'occasion réservé. Exception
   * assumée à la règle des 3 mois d'historique : ni condition d'âge ni
   * ratio d'endettement (le gage protège la banque), mais surprime
   * « jeune entreprise » +0.8 pp avant 90 jours et remise gage −0.4 pp.
   * Pédagogie : l'apprenant découvre les étapes d'une demande de crédit
   * (comparer les taux des 3 banques, mensualité, coût total). */
  computeVehicleFinancingQuote: (bankId, durationMonths = 36) => {
    const state = get();
    const pvf = state.pendingVehicleFinancing;
    if (!pvf || !BANKS[bankId]) return null;
    const amount = pvf.price;
    const rel = state.bankRelations?.[bankId] || {};
    const loyaltyLevel = getLoyaltyLevel(bankId, rel.loansRepaid || 0);
    const totalDebt = (state.credits || []).reduce((s, c) => s + (c.remainingPrincipal || 0), 0);
    let rate = calculateOfferedRate(bankId, {
      reputation: state.reputation || 50,
      vehicleCount: (state.vehicles || []).length,
      monthlyRevenue: state.monthlyRevenues || 0,
      monthlyHistory: state.monthlyHistory || [],
      totalDebt,
      treasury: state.treasury,
      totalDays: state.totalDays || 0,
      loyaltyLevel,
      loanAmount: amount,
      creditScoreGrade: state.creditScore?.grade || null,
    });
    if ((state.totalDays || 0) < 90) rate += 0.008; // surprime jeune entreprise
    rate = Math.max(0.015, rate - 0.004);           // remise gage véhicule
    if (typeof get().hasSupportUnlock === 'function' && get().hasSupportUnlock('negociation_taux')) {
      rate = Math.max(0.015, rate - 0.01);
    }
    const monthly = calculateLoanPayment(amount, rate, durationMonths);
    return {
      eligible: true,
      bankId,
      amount,
      rate,
      ratePercent: (rate * 100).toFixed(2),
      monthly: Math.round(monthly),
      totalCost: Math.round(monthly * durationMonths),
      totalInterest: Math.round(monthly * durationMonths - amount),
      durationMonths,
    };
  },

  requestVehicleFinancingProposals: (durationMonths = 36) =>
    Object.keys(BANKS)
      .map(bankId => get().computeVehicleFinancingQuote(bankId, durationMonths))
      .filter(Boolean),

  acceptVehicleFinancing: (bankId, durationMonths = 36) => {
    const state = get();
    const pvf = state.pendingVehicleFinancing;
    if (!pvf) return false;
    const quote = get().computeVehicleFinancingQuote(bankId, durationMonths);
    if (!quote || !quote.eligible) return false;
    const vehicleData = VEHICLES.find(v => v.id === pvf.vehicleDataId);
    if (!vehicleData) return false;

    const loan = {
      id: generateId(),
      bankId,
      principal: quote.amount,
      annualRate: quote.rate,
      durationMonths,
      monthlyPayment: quote.monthly,
      remainingMonths: durationMonths,
      remainingPrincipal: quote.amount,
      startDay: state.totalDays,
      vehicleFinancing: true,
      label: `Crédit véhicule — ${vehicleData.brand} ${vehicleData.model}`,
    };
    const newVehicle = {
      id: generateId(),
      dataId: vehicleData.id,
      brand: vehicleData.brand,
      model: vehicleData.model,
      tier: vehicleData.tier,
      specialties: vehicleData.specialties,
      price: vehicleData.price,
      maintenancePerMonth: vehicleData.maintenancePerMonth,
      insurancePerMonth: vehicleData.insurancePerMonth,
      dailyFuel: vehicleData.dailyFuel,
      wear: pvf.wear || 25,
      km: Math.round(Math.random() * 80000 + 20000),
      daysActive: 0,
      totalRevenue: 0,
      purchaseDay: state.totalDays,
      status: 'idle',
      assignedDriverId: null,
      assignedContractId: null,
      isUsed: true,
      purchasePrice: pvf.price,
      financedByLoanId: loan.id,
    };
    const rel = { ...(state.bankRelations[bankId] || {}) };
    rel.totalBorrowed = (rel.totalBorrowed || 0) + quote.amount;
    rel.activeLoanCount = (rel.activeLoanCount || 0) + 1;
    if (!rel.firstLoanDay) rel.firstLoanDay = state.totalDays;

    set({
      credits: [...(state.credits || []), loan],
      vehicles: [...(state.vehicles || []), newVehicle],
      // Le prêt couvre exactement le prix : opération neutre en trésorerie.
      treasury: state.treasury + quote.amount - pvf.price,
      bankRelations: { ...state.bankRelations, [bankId]: rel },
      pendingVehicleFinancing: null,
      notifications: [...(state.notifications || []).slice(-30), {
        id: generateId(),
        type: 'vehicle_purchased',
        title: 'Crédit accepté — véhicule livré',
        message: `${BANKS[bankId].name} finance le ${vehicleData.brand} ${vehicleData.model} (${pvf.price.toLocaleString('fr-FR')} € à ${quote.ratePercent} % sur ${durationMonths} mois, ${quote.monthly.toLocaleString('fr-FR')} €/mois). Le véhicule rejoint votre flotte.`,
        day: state.totalDays,
        read: false,
      }],
    });
    if (typeof get().recordDeductibleTva === 'function') {
      get().recordDeductibleTva(pvf.price, 'vehicle_purchase');
    }
    if (typeof get().pushCustomMessage === 'function') {
      // 31/07/2026 — cohérence : DAF si recruté, sinon conseiller/anonyme.
      const financeSender = getFinanceSender(get());
      get().pushCustomMessage({
        senderType: 'advisor',
        senderName: financeSender.senderName,
        subject: 'Crédit véhicule signé — ce qu\'il faut retenir',
        body: `Bien joué !\n\nVous venez de réaliser votre première demande de crédit professionnel :\n\n• CRÉDIT AFFECTÉ : le prêt de ${pvf.price.toLocaleString('fr-FR')} € est gagé sur le véhicule — c'est ce qui a permis à la banque d'accepter malgré notre jeune historique.\n• TAUX : ${quote.ratePercent} % chez ${BANKS[bankId].name} — comparer les 3 banques fait économiser des centaines d'euros d'intérêts.\n• MENSUALITÉ : ${quote.monthly.toLocaleString('fr-FR')} €/mois pendant ${durationMonths} mois — elle s'ajoute à nos charges fixes, à intégrer dans le calcul de rentabilité des contrats.\n• COÛT TOTAL : ${quote.totalCost.toLocaleString('fr-FR')} €, soit ${quote.totalInterest.toLocaleString('fr-FR')} € d'intérêts.\n\nRembourser ce crédit sans incident améliorera notre relation bancaire (fidélité) et nos futurs taux.\n\n${financeSender.signature}`,
        kind: 'info',
      });
    }
    return true;
  },

  declineVehicleFinancing: () => {
    const state = get();
    const pvf = state.pendingVehicleFinancing;
    if (!pvf) return;
    set({
      pendingVehicleFinancing: null,
      notifications: [...(state.notifications || []).slice(-30), {
        id: generateId(),
        type: 'info',
        title: 'Réservation annulée',
        message: `Vous renoncez au ${pvf.brand} ${pvf.model} du confrère. Il le proposera à un autre transporteur.`,
        day: state.totalDays,
        read: false,
      }],
    });
  },

  /**
   * Compute a single-bank loan proposal for buying out a leased vehicle.
   * The vehicle is used as collateral (mortgage) which discounts the rate
   * by 0.4 pp (floored at 1.5 %). The bank's debt-ratio gate is relaxed by
   * +0.5x because the collateral protects the bank against default.
   *
   * Returns null if the vehicle is not leased.
   * Returns { eligible:false, reason } if the bank refuses.
   * Returns { eligible:true, bankId, amount, rate, monthly, totalCost, ... }
   * on success.
   */
  computeBuyoutLoanQuote: (vehicleId, bankId, durationMonths) => {
    const state = get();
    const vehicle = (state.vehicles || []).find(v => v.id === vehicleId);
    if (!vehicle || !vehicle.isLeased) return null;
    const quote = typeof state.computeLeaseBuyout === 'function'
      ? state.computeLeaseBuyout(vehicleId) : null;
    if (!quote) return null;
    const amount = quote.total;

    const bank = BANKS[bankId];
    if (!bank) return { eligible: false, reason: 'Banque inconnue' };

    const totalDebt = (state.credits || []).reduce((s, c) => s + (c.remainingPrincipal || 0), 0);
    const monthlyRevenue = state.monthlyRevenues || 0;
    // 04/02/2026 — Phase Q1 : plafond absolu 6× CA. Plus de floor d'âge.
    const revenueFloor = monthlyRevenue;
    // Mortgage relaxes the cap by +0.5x (rachat sécurisé par le véhicule).
    // eslint-disable-next-line global-require
    const { MAX_DEBT_TO_REVENUE_RATIO } = require('../../data/bankData');
    const maxDebt = (MAX_DEBT_TO_REVENUE_RATIO + 0.5) * revenueFloor;
    if (totalDebt + amount > maxDebt) {
      const ratio = revenueFloor > 0 ? ((totalDebt + amount) / revenueFloor).toFixed(1) : '∞';
      return {
        eligible: false,
        bankId,
        reason: `Endettement post-crédit ${ratio}× CA (plafond ${MAX_DEBT_TO_REVENUE_RATIO + 0.5}× avec hypothèque)`,
        maxDebt,
      };
    }

    const rel = state.bankRelations?.[bankId] || { loansRepaid: 0 };
    const loyaltyLevel = getLoyaltyLevel(bankId, rel.loansRepaid);
    const context = {
      reputation: state.reputation || 50,
      vehicleCount: (state.vehicles || []).length,
      monthlyRevenue,
      totalDebt,
      treasury: state.treasury,
      loyaltyLevel,
      loanAmount: amount,
      creditScoreGrade: state.creditScore?.grade || null,
    };
    let rate = calculateOfferedRate(bankId, context);
    // Tristan Midal DAF unlock: -1 pp.
    if (typeof get().hasSupportUnlock === 'function' && get().hasSupportUnlock('negociation_taux')) {
      rate = rate - 0.01;
    }
    // Mortgage discount: -0.4 pp because the vehicle secures the loan.
    rate = Math.max(0.015, rate - 0.004);
    const monthly = calculateLoanPayment(amount, rate, durationMonths);

    return {
      eligible: true,
      bankId,
      amount,
      rate,
      ratePercent: (rate * 100).toFixed(2),
      monthly: Math.round(monthly),
      totalCost: Math.round(monthly * durationMonths),
      totalInterest: Math.round(monthly * durationMonths - amount),
      durationMonths,
      loyaltyLevel,
      mortgageDiscountPp: 0.4,
    };
  },

  /**
   * Returns the buyout-with-loan quote across all 3 banks (a single helper
   * for the UI). The proposals share the same `amount` (== quote.total) and
   * `durationMonths` but differ in rate / monthly.
   */
  requestBuyoutLoanProposals: (vehicleId, durationMonths) => {
    const proposals = Object.keys(BANKS).map(bankId =>
      get().computeBuyoutLoanQuote(vehicleId, bankId, durationMonths)
    ).filter(Boolean);
    return proposals;
  },

  // Record a loan repayment (called when a loan reaches 0 remaining months)
  recordLoanRepaid: (bankId) => {
    const state = get();
    if (!bankId) return;
    const rel = { ...state.bankRelations[bankId] };
    rel.loansRepaid += 1;
    rel.activeLoanCount = Math.max(0, rel.activeLoanCount - 1);
    rel.loyaltyLevel = getLoyaltyLevel(bankId, rel.loansRepaid);
    set({ bankRelations: { ...state.bankRelations, [bankId]: rel } });
  },

  /**
   * Compute the early repayment quote for a loan.
   * French rule (Code de la consommation L312-21): early-repayment indemnity
   * capped at 3% of remaining principal (and at 6 months of interest at the
   * contractual rate). We apply the stricter of the two caps.
   */
  computeEarlyRepayQuote: (loanId) => {
    const state = get();
    const loan = state.credits.find(c => c.id === loanId);
    if (!loan) return null;
    const principal = Math.max(0, loan.remainingPrincipal || 0);
    const indemnityRule = principal * 0.03;
    const sixMonthsInterest = principal * (loan.annualRate || 0) / 2;
    const indemnity = Math.round(Math.min(indemnityRule, sixMonthsInterest));
    const total = Math.round(principal + indemnity);
    return { principal: Math.round(principal), indemnity, total };
  },

  /**
   * Early-repay a loan in full. Charges remainingPrincipal × 1.03 (capped at
   * 6 months of contractual interest) and removes the loan from the books.
   * Counts as a repaid loan for bank loyalty.
   */
  earlyRepayLoan: (loanId) => {
    const state = get();
    const loan = state.credits.find(c => c.id === loanId);
    if (!loan) return false;
    const quote = get().computeEarlyRepayQuote(loanId);
    if (!quote) return false;
    if (state.treasury < quote.total) return false;

    const updatedRelations = { ...state.bankRelations };
    if (loan.bankId && updatedRelations[loan.bankId]) {
      const rel = { ...updatedRelations[loan.bankId] };
      rel.loansRepaid += 1;
      rel.activeLoanCount = Math.max(0, rel.activeLoanCount - 1);
      rel.loyaltyLevel = getLoyaltyLevel(loan.bankId, rel.loansRepaid);
      updatedRelations[loan.bankId] = rel;
    }

    const notification = {
      id: generateId(),
      type: 'loan_repaid',
      title: 'Crédit soldé',
      message: `Vous avez remboursé par anticipation ${quote.principal.toLocaleString('fr-FR')} € (+ ${quote.indemnity.toLocaleString('fr-FR')} € indemnité).`,
      day: state.totalDays,
      read: false,
    };

    set({
      credits: state.credits.filter(c => c.id !== loanId),
      treasury: state.treasury - quote.total,
      bankRelations: updatedRelations,
      // Release mortgage on any vehicle backed by this loan.
      vehicles: (state.vehicles || []).map(v =>
        v.mortgageLoanId === loanId
          ? { ...v, mortgaged: false, mortgageLoanId: null }
          : v
      ),
      notifications: [...(state.notifications || []).slice(-30), notification],
    });
    return quote;
  },

  // Subscribe to a placement
  subscribePlacement: (bankId, amount, product) => {
    const state = get();
    if (!product || state.treasury < amount || amount < product.minAmount) return false;

    const placement = {
      id: generateId(),
      bankId,
      name: product.name,
      amount,
      rate: product.rate,
      startDay: state.totalDays,
      term: product.termMonths || 0,
      liquid: product.liquid,
      monthlyInterest: Math.round(amount * product.rate / 12),
      taxBonus: product.taxBonus || 0,
    };

    // Track first contact day on the bank relation (used by the seniority
    // loyalty bonus and the "Client depuis N mois" badge).
    const rel = { ...(state.bankRelations?.[bankId] || { loansRepaid: 0, totalBorrowed: 0, activeLoanCount: 0, firstLoanDay: null, loyaltyLevel: 0 }) };
    if (!rel.firstLoanDay) rel.firstLoanDay = state.totalDays;

    set({
      activePlacements: [...state.activePlacements, placement],
      treasury: state.treasury - amount,
      bankRelations: { ...(state.bankRelations || {}), [bankId]: rel },
    });
    return true;
  },

  // Withdraw a liquid placement
  withdrawPlacement: (placementId) => {
    const state = get();
    const placement = state.activePlacements.find(p => p.id === placementId);
    if (!placement) return false;
    if (!placement.liquid) {
      // Check if term has elapsed
      const monthsElapsed = Math.floor((state.totalDays - placement.startDay) / 30);
      if (monthsElapsed < placement.term) return false;
    }

    // Return principal + accrued interest
    const monthsHeld = Math.floor((state.totalDays - placement.startDay) / 30);
    const interest = Math.round(placement.amount * placement.rate * monthsHeld / 12);

    set({
      activePlacements: state.activePlacements.filter(p => p.id !== placementId),
      treasury: state.treasury + placement.amount + interest,
    });
    return true;
  },

  // Monthly interest from placements (called from nextDay at month boundary)
  collectPlacementInterest: () => {
    const state = get();
    const totalInterest = state.activePlacements.reduce((s, p) => s + p.monthlyInterest, 0);
    if (totalInterest > 0) {
      set({ treasury: state.treasury + totalInterest });
    }
    return totalInterest;
  },

  // Recalculate credit score (called monthly after month 3)
  updateCreditScore: () => {
    const state = get();
    const totalDebt = (state.credits || []).reduce((s, c) => s + (c.remainingPrincipal || 0), 0);
    const loansRepaidTotal = Object.values(state.bankRelations).reduce((s, r) => s + r.loansRepaid, 0);
    const premiums = (state.vehicles || []).map(v => v.insuranceCoverage || 'standard');
    const premiumInsuranceRatio = premiums.length > 0
      ? premiums.filter(p => p === 'premium').length / premiums.length
      : 0;

    const scoreData = calculateCreditScore({
      reputation: state.reputation || 50,
      treasury: state.treasury,
      monthlyRevenue: state.monthlyRevenues || 0,
      totalDebt,
      vehicleCount: (state.vehicles || []).length,
      driversCount: (state.drivers || []).length,
      totalDays: state.totalDays,
      loansRepaidTotal,
      premiumInsuranceRatio,
    });

    set({ creditScore: scoreData });
    return scoreData;
  },

  // Check if score should be revealed (at month 3)
  checkScoreReveal: () => {
    const state = get();
    if (state.scoreRevealed) return; // Already revealed
    if (state.totalDays < 90) return; // Not yet 3 months

    // Calculate score
    const totalDebt = (state.credits || []).reduce((s, c) => s + (c.remainingPrincipal || 0), 0);
    const loansRepaidTotal = Object.values(state.bankRelations).reduce((s, r) => s + r.loansRepaid, 0);
    const premiums = (state.vehicles || []).map(v => v.insuranceCoverage || 'standard');
    const premiumInsuranceRatio = premiums.length > 0
      ? premiums.filter(p => p === 'premium').length / premiums.length
      : 0;
    const scoreData = calculateCreditScore({
      reputation: state.reputation || 50,
      treasury: state.treasury,
      monthlyRevenue: state.monthlyRevenues || 0,
      totalDebt,
      vehicleCount: (state.vehicles || []).length,
      driversCount: (state.drivers || []).length,
      totalDays: state.totalDays,
      loansRepaidTotal,
      premiumInsuranceRatio,
    });

    // Pick the advisor: use the bank where the player has the most interaction, or first loan bank
    let advisorBankId = 'bnb_mariba'; // Default
    const initialLoan = state.credits.find(c => c.bankId);
    if (initialLoan) advisorBankId = initialLoan.bankId;
    // Or highest loyalty
    let maxLoans = 0;
    Object.entries(state.bankRelations).forEach(([bId, rel]) => {
      if (rel.totalBorrowed > maxLoans) { maxLoans = rel.totalBorrowed; advisorBankId = bId; }
    });

    const messages = SCORE_REVEAL_MESSAGES[advisorBankId];
    const message = messages ? messages[scoreData.grade] : null;

    set({
      creditScore: scoreData,
      scoreRevealed: true,
      scoreRevealMessage: {
        bankId: advisorBankId,
        advisorName: BANKS[advisorBankId]?.advisor.name,
        advisorPortrait: BANKS[advisorBankId]?.advisor.portrait,
        bankName: BANKS[advisorBankId]?.name,
        bankColor: BANKS[advisorBankId]?.color,
        message,
        grade: scoreData.grade,
        score: scoreData.score,
      },
      scoreRevealDismissed: false,
    });
  },

  dismissScoreReveal: () => {
    set({ scoreRevealDismissed: true });
  },
});
